package com.crowpay.views.theming

import com.crowpay.views.components.nav.AppDimensions
import com.lightningkite.kiteui.models.ButtonSemantic
import com.lightningkite.kiteui.models.CardSemantic
import com.lightningkite.kiteui.models.Color
import com.lightningkite.kiteui.models.CornerRadii
import com.lightningkite.kiteui.models.DisabledSemantic
import com.lightningkite.kiteui.models.EmphasizedSemantic
import com.lightningkite.kiteui.models.SelectedSemantic
import com.lightningkite.kiteui.models.Semantic
import com.lightningkite.kiteui.models.Theme
import com.lightningkite.kiteui.models.ThemeAndBack
import com.lightningkite.kiteui.models.UnselectedSemantic
import com.lightningkite.kiteui.models.dp
import com.lightningkite.kiteui.models.px
import com.lightningkite.kiteui.models.rem

data object FormHeader : Semantic {
    override val key: String = "frmhdr"
    override fun default(theme: Theme): ThemeAndBack = theme.copy(
        id = key,
        background = Color.fromHex(0x545454),
        foreground = Color.white,
    ).withBack
}

data object FormMessage : Semantic {
    override val key: String = "frmmsg"
    override fun default(theme: Theme): ThemeAndBack = theme.copy(
        id = key,
        outlineWidth = 1.px,
        derivations = mapOf(
            CardSemantic to { it[LightSection].theme.copy(id = "crd", spacing = 1.rem).withBack },
            EmphasizedSemantic to {
                it.copy(
                    id = "emph",
                    background = AppColors.primary.light1,
                    foreground = Color.white,
                    spacing = 0.75.rem,
                    cornerRadii = CornerRadii.Constant(1.rem),
                    outlineWidth = 0.px
                ).withBack
            }
        )
    ).withBackNoPadding
}

data object NotReadyButton : Semantic {
    override val key: String = "ntrdybtn"
    override fun default(theme: Theme): ThemeAndBack = theme[ButtonSemantic].theme.copy(
        id = key,
        background = Color.fromHex(0x545454),
        foreground = Color.white,
    ).withBack
}

data object ReportIncidentButton : Semantic {
    override val key: String = "rptinc"
    override fun default(theme: Theme): ThemeAndBack = theme[NotReadyButton]
}

data object IssueButton : Semantic {
    override val key: String = "isubtn"
    override fun default(theme: Theme): ThemeAndBack = WarningButtonSemantic().invoke(theme)
}

data object DisputeButton : Semantic {
    override val key: String = "dsptbtn"
    override fun default(theme: Theme): ThemeAndBack = theme[DangerButtonSemantic].theme.copy(
        id = key,
        derivations = mapOf(
            UnselectedSemantic to {
                it.copy(
                    id = "unsel",
                    outline = AppColors.red.main,
                    outlineWidth = 2.px,
                    background = Color.white
                ).withBack
            },
            SelectedSemantic to {
                it.copy(
                    id = "sel",
                    background = Color.white,
                    outline = AppColors.grey.light2,
                    outlineWidth = 1.px,
                ).withBack
            }
        )
    ).withBack
}

data object ConcernCard : Semantic {
    override val key: String = "cncrn"
    override fun default(theme: Theme): ThemeAndBack = theme[LightSection].theme.copy(
        id = key,
        derivations = mapOf(
            MessageSemantic(mine = true) to {
                it.copy(
                    id = "crd",
                    background = Color.white
                ).withBack
            },
            MessageSemantic(mine = false) to {
                it.copy(
                    id = "crd",
                    background = Color.white
                ).withBack
            },
            DisabledSemantic to { it.withoutBack },
            EmphasizedSemantic to {
                it.copy(
                    id = "emph",
                    font = it.font.copy(underline = true)
                ).withoutBack
            }
        )
    ).withBack
}

data object LargeSelectionCards : Semantic {
    override val key: String = "lrgsel"
    override fun default(theme: Theme): ThemeAndBack = theme.copy(
        id = key,
        cornerRadii = CornerRadii.Constant(1.rem),
        spacing = AppDimensions.sectionIndent,
        derivations = mapOf(
            CardSemantic to {
                it.copy(
                    id = "crd",
                    background = Color.white,
                ).withBack
            },
            UnselectedSemantic to {
                it.copy(
                    id = "unsel",
                    outline = AppColors.grey.light1,
                    outlineWidth = 1.px,
                ).withBack
            },
            SelectedSemantic to {
                it.copy(
                    id = "sel",
                    outline = AppColors.red.main,
                    outlineWidth = 2.px,
                ).withBack
            }
        )
    ).withoutBack
}

data object ModernDialog : Semantic {
    override val key: String = "mdndlg"
    override fun default(theme: Theme): ThemeAndBack = theme.copy(
        id = key,
        background = AppColors.grey.light3,
        elevation = 1.dp,
        spacing = AppDimensions.sectionIndent,
        outlineWidth = 0.dp,
        cornerRadii = CornerRadii.ForceConstant(1.rem),
        revert = true,
    ).withBack
}